import serverRequest from './serverRequest';
import { REFRESH_LOCK_NAME, REFRESH_REVISION_KEY } from '../constants/network';
// src/utils/api.js
import { BILLING, CALCUL, FORMATIONS, MARKETING_LEADS, MISSION_DRAFTS, NATIONAL_EXAMS, NOTION_PRACTICE, PLACEMENT, STATISTICS } from '../constants/apiRoutes';
import { FAMILY_API, MISSION_STATUS_FILTER } from '../constants/familySpace';
import { MISSION_COMPLETION_API } from '../constants/missionCompletion';
import { SETTINGS_API } from '../constants/settings';
import { INBOX_API } from '../constants/inbox';
import { PORTFOLIO } from '../constants/portfolio';
import { GUIDE_COPY } from '../constants/guide_messages.fr';
import { PUBLIC_OFFER } from '../constants/publicOffer';

const LOCAL_API = 'http://localhost:5000/api';
const PRODUCTION_API = 'https://api.neoprof.fr/api';
const SESSION_EXPIRED_EVENT = 'session-expired';
const SESSION_RESTORED_EVENT = 'session-restored';

// The singleton is built at import time, also during the marketing prerender (no browser).
const hasBrowser = () => typeof window !== 'undefined';
const readStorage = (key) => (hasBrowser() ? window.localStorage.getItem(key) : null);
// App events for the UI. The prerender runs this client in Node, where there is no window to notify.
const dispatchAppEvent = (name, detail) => {
  if (hasBrowser()) window.dispatchEvent(new CustomEvent(name, { detail }));
};

// Detect if running on Vercel/production (not localhost). Server render → production.
const isDeployedEnvironment = () => {
  if (!hasBrowser()) return true;
  return window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
};

const WRITE_METHODS = ['POST', 'PUT', 'DELETE', 'PATCH'];
const SANDBOX_REFUSED = GUIDE_COPY.sandboxRefused;

class ApiService {
  constructor() {
    // JWT is NOT stored in localStorage — httpOnly cookie is the auth mechanism.
    // this.token is kept in memory only (populated on login, lost on page refresh — intentional).
    this.token = null;
    // CSRF token for state-changing requests (readable by JS, needed to send as header)
    this.csrfToken = readStorage('csrf_token');
    // If deployed (Vercel), always use production API
    // If local, check localStorage setting
    this.isProductionMode = isDeployedEnvironment() || readStorage('use_production_api') === 'true';
    // Callback for session expiration (set by App.js)
    this.onSessionExpired = null;
    this._sessionExpiredAnnounced = false;
    // Shared refresh promise — prevents concurrent 401s from triggering multiple /auth/refresh calls
    this._refreshPromise = null;
  }

  /** Guide demo séance: while on, every write is refused before leaving the browser (useCourseApi); `readOnly` requests pass. */
  setSandbox(on) {
    this.sandbox = on;
  }

  // Register callback for session expiration overlay
  setSessionExpiredCallback(callback) {
    this.onSessionExpired = callback;
  }

  _announceSessionExpired() {
    if (this._sessionExpiredAnnounced) return;
    this._sessionExpiredAnnounced = true;
    dispatchAppEvent(SESSION_EXPIRED_EVENT);
    this.onSessionExpired?.();
  }

  _announceSessionRestored() {
    if (!this._sessionExpiredAnnounced) return;
    this._sessionExpiredAnnounced = false;
    dispatchAppEvent(SESSION_RESTORED_EVENT);
  }

  // Silent token refresh — calls /auth/refresh using the httpOnly refresh token cookie.
  // All concurrent callers share the same in-flight promise so /auth/refresh is called only once.
  async silentRefresh() {
    if (this._refreshPromise) {
      return this._refreshPromise;
    }
    const refresh = () => serverRequest(`${this.getApiBase()}/auth/refresh`, {
      interactive: false,
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    }, async response => {
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Refresh failed');
      }
      const data = await response.json();
      // Sync local session timestamps so useTokenMonitor stays accurate
      if (data.session_expires_at) {
        localStorage.setItem('session_expires_at', String(data.session_expires_at));
      }
      if (data.session_token_lifetime) {
        localStorage.setItem('session_token_lifetime', String(data.session_token_lifetime));
        // Keep session_issued_at in sync so the URSSAF payment age check stays correct
        localStorage.setItem('session_issued_at', String(data.session_expires_at - data.session_token_lifetime));
      }
      if (data.inactivity_timeout_seconds) {
        localStorage.setItem('inactivity_timeout_seconds', String(data.inactivity_timeout_seconds));
      }
      if (data.csrf_token) {
        this.setCsrfToken(data.csrf_token);
      }
      localStorage.setItem(REFRESH_REVISION_KEY, `${Date.now()}:${Math.random()}`);
      this._announceSessionRestored();
    });
    const revision = readStorage(REFRESH_REVISION_KEY);
    const coordinated = async () => {
      if (typeof navigator !== 'undefined' && navigator.locks?.request) {
        return navigator.locks.request(REFRESH_LOCK_NAME, async () => {
          const updated = readStorage(REFRESH_REVISION_KEY);
          if (updated && updated !== revision && Number(readStorage('session_expires_at')) > Date.now() / 1000) {
            this.csrfToken = readStorage('csrf_token');
            this._announceSessionRestored();
            return;
          }
          return refresh();
        });
      }
      return refresh();
    };
    this._refreshPromise = coordinated().finally(() => { this._refreshPromise = null; });
    return this._refreshPromise;
  }

  // Returns true only when user manually switched to production from localhost
  // (for showing warnings). Returns false when deployed to Vercel.
  isManualProductionMode() {
    return !isDeployedEnvironment() && this.isProductionMode;
  }

  // Returns true if running on Vercel/deployed environment
  isDeployed() {
    return isDeployedEnvironment();
  }

  getApiBase() {
    return this.isProductionMode ? PRODUCTION_API : LOCAL_API;
  }

  setProductionMode(useProduction) {
    this.isProductionMode = useProduction;
    localStorage.setItem('use_production_api', useProduction.toString());
  }

  isUsingProduction() {
    return this.isProductionMode;
  }

  setToken(token) {
    // Keep in memory only — never write the JWT to localStorage.
    // Decode exp/iat to store non-sensitive timing data for UX (expiry warnings, age checks).
    this.token = token;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.exp) localStorage.setItem('session_expires_at', String(payload.exp));
      if (payload.iat) localStorage.setItem('session_issued_at', String(payload.iat));
      // Store lifetime so sliding-session sync can update session_expires_at after each request
      if (payload.exp && payload.iat) {
        localStorage.setItem('session_token_lifetime', String(payload.exp - payload.iat));
      }
    } catch { /* ignore malformed token */ }
  }

  setCsrfToken(csrfToken) {
    this.csrfToken = csrfToken;
    localStorage.setItem('csrf_token', csrfToken);
  }

  // Single source of truth for persisting session timing from any auth response
  // (login, magic-link verify, passkey verify, register). Without this, the
  // token monitor and inactivity monitor don't know when the session expires
  // and misbehave (e.g. the SessionExpiredOverlay can fire spuriously).
  _persistSessionTiming(data) {
    if (!data) return;
    if (data.session_expires_at) {
      localStorage.setItem('session_expires_at', String(data.session_expires_at));
    }
    if (data.session_token_lifetime) {
      localStorage.setItem('session_token_lifetime', String(data.session_token_lifetime));
      if (data.session_expires_at) {
        localStorage.setItem('session_issued_at', String(data.session_expires_at - data.session_token_lifetime));
      }
    }
    if (data.inactivity_timeout_seconds) {
      localStorage.setItem('inactivity_timeout_seconds', String(data.inactivity_timeout_seconds));
    }
    if (data.csrf_token) {
      this.setCsrfToken(data.csrf_token);
    }
  }

  clearToken() {
    this.token = null;
    this.csrfToken = null;
    localStorage.removeItem('session_expires_at');
    localStorage.removeItem('session_issued_at');
    localStorage.removeItem('session_token_lifetime');
    localStorage.removeItem('inactivity_timeout_seconds');
    localStorage.removeItem('csrf_token');
    localStorage.removeItem(REFRESH_REVISION_KEY);
  }

  getHeaders() {
    return { 'Content-Type': 'application/json' };
  }

  async request(endpoint, options = {}) {
    const url = `${this.getApiBase()}${endpoint}`;
    const headers = {
      ...(options.body != null ? this.getHeaders() : {}),
      ...options.headers
    };
    if (options.body instanceof FormData) delete headers['Content-Type'];

    // Add CSRF token for state-changing requests
    const method = (options.method || 'GET').toUpperCase();
    if (this.sandbox && WRITE_METHODS.includes(method) && !options.readOnly) {
      throw Object.assign(new Error(SANDBOX_REFUSED), { status: 0, sandbox: true });
    }
    if (WRITE_METHODS.includes(method) && this.csrfToken) {
      headers['X-CSRF-Token'] = this.csrfToken;
    }

    const config = {
      ...options,
      credentials: 'include', // Include cookies (httpOnly auth token) with requests
      headers
    };

    try {
      return await serverRequest(url, config, async (response) => {

      // Handle 401 Unauthorized
      if (response.status === 401) {
        const data = await response.json().catch(() => ({}));
        const errorMessage = data.message || data.error || 'Session expired';

        // Login endpoint: 401 = wrong credentials — let the form handle it, never show overlay.
        if (endpoint === '/auth/teacher-login') {
          throw new Error(errorMessage);
        }
        // Family space (PWA1): its own device cookie, never the teacher session.
        // A 401 means "unknown device" and the caller shows the code entry.
        if (options.skipSessionHandling) {
          const error = new Error(errorMessage);
          error.status = 401;
          throw error;
        }

        // All other endpoints: the user was authenticated. Whatever caused the 401
        // (token expired, device mismatch, revoked, hard cap, etc.) → try a silent
        // refresh first. If that succeeds, replay the request transparently.
        // If it fails, show the overlay — React state is fully preserved.
        // NEVER hard-redirect: that destroys all in-progress work.
        if (this.onSessionExpired && !options._isRetry) {
          try {
            await this.silentRefresh();
            return this.request(endpoint, { ...options, _isRetry: true });
          } catch (refreshError) {
            if (refreshError.isNetworkError) throw refreshError;
            this._announceSessionExpired();
            const error = new Error(errorMessage);
            error.isSessionExpired = true;
            throw error;
          }
        } else {
          // Already retried once (or no callback registered) — show overlay or throw
          if (this.onSessionExpired) {
            this._announceSessionExpired();
          }
          const error = new Error(errorMessage);
          error.isSessionExpired = true;
          throw error;
        }
      }

      const data = await response.json();

      if (!response.ok) {
        let errMsg = data.message || data.error || `HTTP error! status: ${response.status}`;
        // Append URSSAF structured errors when present (e.g. ERR_LIEN_PARTICULIER_PRESTATAIRE)
        if (Array.isArray(data.urssaf_errors) && data.urssaf_errors.length > 0) {
          const urssafDetail = data.urssaf_errors.map(e => e.message || e.code).filter(Boolean).join(' | ');
          errMsg = errMsg !== `HTTP error! status: ${response.status}` ? `${errMsg} — ${urssafDetail}` : urssafDetail;
        }
        const err = new Error(errMsg);
        err.data   = data;
        err.status = response.status;
        if (!options.suppressProRedirect && response.status === 402 && data?.data?.code === 'PRO_REQUIRED') {
          dispatchAppEvent('billing-pro-required', data.data);
        }
        throw err;
      }

      // Server responded - notify NetworkContext it's reachable
      dispatchAppEvent('server-reachable');
      this._announceSessionRestored();

      // Unwrap standardized response envelope {status, message, data, errors} transparently.
      // Components continue to read the same keys as before — no changes needed downstream.
      if (data && typeof data === 'object' && 'status' in data && 'data' in data) {
        return data.data;
      }
      return data;
      });
    } catch (error) {
      // Don't log session expired errors (they're expected)
      if (!error.isSessionExpired) {
        console.error(`API request failed: ${endpoint}`, error);
      }
      throw error;   // serverRequest already reported transport failures to NetworkContext
    }
  }

  // ── Portfolio maker (/teacher/portfolio*) ──
  portfolioOverview() { return this.request(PORTFOLIO.OVERVIEW, { suppressProRedirect: true }); }
  savePortfolioSite(hostSlug) { return this.request(PORTFOLIO.SITE, { method: 'PUT', body: JSON.stringify({ host_slug: hostSlug }) }); }
  deletePortfolioSite() { return this.request(PORTFOLIO.SITE, { method: 'DELETE' }); }
  createPortfolio(content) { return this.request(PORTFOLIO.PAGES, { method: 'POST', body: JSON.stringify({ content }) }); }
  getPortfolio(id) { return this.request(PORTFOLIO.PAGE(id)); }
  savePortfolio(id, version, content) { return this.request(PORTFOLIO.PAGE(id), { method: 'PUT', body: JSON.stringify({ version, content }) }); }
  deletePortfolio(id) { return this.request(PORTFOLIO.PAGE(id), { method: 'DELETE' }); }
  publishPortfolio(id, version) { return this.request(PORTFOLIO.PUBLISH(id), { method: 'POST', body: JSON.stringify({ version }) }); }
  unpublishPortfolio(id, version) { return this.request(PORTFOLIO.UNPUBLISH(id), { method: 'POST', body: JSON.stringify({ version }) }); }
  uploadPortfolioImage(image) {
    const body = new FormData();
    body.append('image', image);
    return this.request(PORTFOLIO.MEDIA, { method: 'POST', body });
  }
  /** Owner image as a Blob: the preview iframe cannot rely on cross-site cookies on <img>. */
  getPortfolioImage(mediaId) {
    return serverRequest(`${this.getApiBase()}${PORTFOLIO.PRIVATE_MEDIA(mediaId)}`, { credentials: 'include' }, async (response) => {
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return response.blob();
    });
  }
  /** Anonymous read of a published page; no cookies, no session handling. */
  getPublicPortfolio(host, subject = '') {
    return serverRequest(`${this.getApiBase()}${PORTFOLIO.PUBLIC(host, subject)}`, { credentials: 'omit' }, async (response) => {
      const envelope = await response.json().catch(() => ({}));
      if (!response.ok) {
        const error = new Error(envelope.message || `HTTP error! status: ${response.status}`);
        error.status = response.status;
        throw error;
      }
      return envelope.data;
    });
  }
  publicPortfolioImageUrl(mediaId) { return `${this.getApiBase()}${PORTFOLIO.PUBLIC_MEDIA(mediaId)}`; }

  // Generic convenience methods (used by EmailComposer, PendingPaymentsModal, etc.)
  get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  post(endpoint, body, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // Authentication
  getBillingEntitlement() { return this.get(BILLING.ENTITLEMENT); }
  getBillingSummary() { return this.get(BILLING.SUMMARY); }
  markTrialEndNoticeSeen() { return this.post(BILLING.TRIAL_END_NOTICE, {}); }
  startBillingCheckout(interval, tier = 'pro') { return this.post(BILLING.CHECKOUT, { interval, tier }); }
  getBillingCheckoutStatus(sessionId) { return this.get(BILLING.CHECKOUT_STATUS(sessionId)); }
  openBillingPortal() { return this.post(BILLING.PORTAL, {}); }
  getBillingReferral() { return this.get(BILLING.REFERRAL); }

  async register(registrationData) {
    const response = await this.request('/teacher/register', {
      method: 'POST',
      skipSessionHandling: true,
      body: JSON.stringify(registrationData)
    });
    this._persistSessionTiming(response);
    return response;
  }

  // Unified email-first entry. Returns one of:
  //   { action: 'unknown_email' }
  //   { action: 'needs_magic_link', kind: 'login_no_passkey' }
  //   { action: 'passkey', challenge, challenge_token, credential_ids }
  // No side effects — no email is sent from this endpoint.
  async authEntry(email) {
    return this.request('/auth/entry', {
      method: 'POST',
      skipSessionHandling: true,
      body: JSON.stringify({ email }),
    });
  }

  // Fallback after passkey failure. Server dispatches login OR register magic link.
  async getPublicOffer() {
    return this.request(PUBLIC_OFFER.path, { skipSessionHandling: true });
  }

  async requestMagicLink(email, { attribution } = {}) {
    return this.request('/auth/magic-link/request', {
      method: 'POST',
      skipSessionHandling: true,
      body: JSON.stringify({ email, ...(attribution ? { attribution } : {}) })
    });
  }

  async verifyMagicLink(token, { trustedDevice = true } = {}) {
    const response = await this.request('/auth/magic-link/verify', {
      method: 'POST',
      skipSessionHandling: true,
      body: JSON.stringify({ token, trusted_device: trustedDevice })
    });
    this._persistSessionTiming(response);
    return response;
  }

  // 6-digit OTP companion to the magic link. Same backend row, same trust level.
  async verifyCode(email, code, { trustedDevice = true } = {}) {
    const response = await this.request('/auth/code/verify', {
      method: 'POST',
      skipSessionHandling: true,
      body: JSON.stringify({ email, code, trusted_device: trustedDevice })
    });
    this._persistSessionTiming(response);
    return response;
  }

  async verifyRegisterCode(email, code) {
    return this.request('/auth/register-code/verify', {
      method: 'POST',
      skipSessionHandling: true,
      body: JSON.stringify({ email, code }),
    });
  }

  // Validates a register-magic-link token; returns { email, pending_id } for form pre-fill.
  async verifyRegisterToken(token) {
    return this.request('/auth/register-magic-link/verify', {
      method: 'POST',
      skipSessionHandling: true,
      body: JSON.stringify({ token }),
    });
  }

  // Submits a WebAuthn assertion + the challenge_token issued by /auth/entry.
  // Returns full session on success.
  async verifyPasskey({ email, challengeToken, assertion, trustedDevice = true }) {
    const response = await this.request('/auth/passkey/verify', {
      method: 'POST',
      skipSessionHandling: true,
      body: JSON.stringify({
        email,
        challenge_token: challengeToken,
        assertion,
        trusted_device: trustedDevice,
      }),
    });
    this._persistSessionTiming(response);
    return response;
  }

  async beginDiscoverablePasskey() {
    return this.request('/auth/passkey/discover/begin', { method: 'POST', skipSessionHandling: true });
  }

  async verifyDiscoverablePasskey({ challengeToken, assertion, trustedDevice = true }) {
    const response = await this.request('/auth/passkey/discover/verify', {
      method: 'POST',
      skipSessionHandling: true,
      body: JSON.stringify({
        challenge_token: challengeToken,
        assertion,
        trusted_device: trustedDevice,
      }),
    });
    this._persistSessionTiming(response);
    return response;
  }

  // WebAuthn registration (authenticated — teacher enrolling a new passkey).
  async beginPasskeyRegistration() {
    return this.request('/auth/passkey/register/begin', { method: 'POST' });
  }

  async completePasskeyRegistration({ challengeToken, credential, name }) {
    return this.request('/auth/passkey/register/complete', {
      method: 'POST',
      body: JSON.stringify({
        challenge_token: challengeToken,
        credential,
        name: name || null,
      }),
    });
  }

  async listPasskeys() {
    return this.request('/auth/passkey/list');
  }

  async deletePasskey(credentialIdB64) {
    return this.request(`/auth/passkey/${encodeURIComponent(credentialIdB64)}`, {
      method: 'DELETE',
    });
  }

  renamePasskey(credentialIdB64, name) {
    return this.request(`/auth/passkey/${encodeURIComponent(credentialIdB64)}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    });
  }

  // NOTIF1 T13: inbox summary {p1, p2, unread}; actions {items, total}; feed {items, next_cursor}.
  getInboxSnapshot() { return this.get(INBOX_API.SNAPSHOT); }
  getInboxSummary() { return this.get(INBOX_API.SUMMARY); }
  getInboxActions(limit) { return this.get(limit ? `${INBOX_API.ACTIONS}?limit=${limit}` : INBOX_API.ACTIONS); }
  snoozeInboxAction(key, days) { return this.post(INBOX_API.SNOOZE(key), { days }); }
  dismissInboxAction(key) { return this.post(INBOX_API.DISMISS(key), {}); }

  getNotifications({ cursor, limit, types = [] } = {}) {
    const params = new URLSearchParams();
    if (cursor) params.set('cursor', cursor);
    if (limit) params.set('limit', String(limit));
    types.forEach((type) => params.append('type', type));
    const query = params.toString();
    return this.get(query ? `${INBOX_API.NOTIFICATIONS}?${query}` : INBOX_API.NOTIFICATIONS);
  }

  /** `upTo` = cursor of the newest item displayed; newer rows stay unread. */
  markNotificationsRead(upTo) { return this.post(INBOX_API.NOTIFICATIONS_READ, { up_to: upTo }); }

  /** 409 when already decided or `version` is stale. Returns the decided request (+ lesson fields). */
  decideScheduleRequest(requestId, decision, version) {
    return this.post(INBOX_API.DECISION(requestId), { decision, version });
  }

  // NOTIF1 T12: email toggles per event type — [{event_type, email_enabled}].
  getNotificationPrefs() { return this.get(SETTINGS_API.NOTIFICATION_PREFS); }

  setMissionTerm(missionTerm) {
    return this.request(SETTINGS_API.MISSION_TERM, {
      method: 'PUT',
      body: JSON.stringify({ mission_term: missionTerm }),
    });
  }

  setNotificationPref(eventType, emailEnabled) {
    return this.request(SETTINGS_API.NOTIFICATION_PREFS, {
      method: 'PUT',
      body: JSON.stringify({ event_type: eventType, email_enabled: emailEnabled }),
    });
  }

  // Public landing simulator lead (LAND3): visitor numbers mailed back, consent required.
  async submitLead({ email, consent, inputs, results }) {
    return this.request(MARKETING_LEADS.SUBMIT, {
      method: 'POST',
      body: JSON.stringify({ email, consent, inputs, results }),
    });
  }

  // Public contact form. Honeypot: `website` field — bots fill it, humans never see it.
  async sendContactMessage({ topic, name, email, message, website }) {
    return this.request('/contact', {
      method: 'POST',
      body: JSON.stringify({ topic, name, email, message, website: website || '' }),
    });
  }

  async login(username, password) {
    const data = await this.request('/auth/teacher-login', {
      method: 'POST',
      body: JSON.stringify({ username, password })
    });
    // JWT is in the httpOnly cookie — store only timing metadata for UX
    this._persistSessionTiming(data);
    return data;
  }

  // Signal to backend that the user is inactive — revokes the refresh token server-side.
  // Uses the refresh_token httpOnly cookie directly (no access token needed).
  // Called by useInactivityMonitor as fire-and-forget.
  async signalInactive() {
    return serverRequest(`${this.getApiBase()}/auth/signal-inactive`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
  }

  // Fetch a fresh CSRF token (call after page reload if needed)
  async refreshCsrfToken() {
    try {
      const data = await this.request('/auth/csrf-token');
      if (data.csrf_token) {
        this.setCsrfToken(data.csrf_token);
      }
      return data.csrf_token;
    } catch (error) {
      console.error('Failed to refresh CSRF token:', error);
      return null;
    }
  }

  async logout() {
    try {
      await this.request('/auth/logout', { method: 'POST' });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.clearToken();
    }
  }

  async getTeacherInfo({ skipSessionHandling = false } = {}) {
    return this.request('/teacher/info', { skipSessionHandling });
  }

  /** ST5: template email (relance | silent) to families, one email each. */
  sendFamilyEmails({ templateKey, familyIds, subject, body }) {
    return this.post('/email-templates/send', { template_key: templateKey, family_ids: familyIds, custom_subject: subject, custom_body: body });
  }

  async updateTeacherProfile(fields) {
    return this.request('/teacher/profile', {
      method: 'PUT',
      body: JSON.stringify(fields)
    });
  }

  async markProfileAutopromptSeen() {
    return this.request('/teacher/profile-autoprompt-seen', { method: 'POST' });
  }

  async getLessonQuestions() {
    return this.request('/lesson-questions');
  }

  async updateLessonQuestions(questions) {
    return this.request('/lesson-questions', {
      method: 'PUT',
      body: JSON.stringify({ questions })
    });
  }

  // Families
  async getFamilies(includeArchived = false) {
    const endpoint = includeArchived ? '/families?include_archived=true' : '/families';
    return this.request(endpoint);
  }

  async downloadRosterTemplate() {
    const response = await serverRequest(`${this.getApiBase()}/roster-import/template`, { credentials: 'include' });
    if (!response.ok) throw new Error('Téléchargement du modèle impossible.');
    return response.blob();
  }

  async previewRosterImport(file) {
    const body = new FormData();
    body.append('file', file);
    return this.request('/roster-import/preview', { method: 'POST', body });
  }

  async importRoster(file) {
    const body = new FormData();
    body.append('file', file);
    return this.request('/roster-import', { method: 'POST', body });
  }

  async createFamily(familyData) {
    return this.request('/families', {
      method: 'POST',
      body: JSON.stringify(familyData)
    });
  }

  async updateFamily(familyId, familyData) {
    return this.request(`/families/${familyId}`, {
      method: 'PUT',
      body: JSON.stringify(familyData)
    });
  }

  async deleteFamily(familyId) {
    return this.request(`/families/${familyId}`, {
      method: 'DELETE'
    });
  }

  async archiveFamily(familyId) {
    return this.request(`/families/${familyId}`, {
      method: 'DELETE'
    });
  }

  async unarchiveFamily(familyId) {
    return this.request(`/families/${familyId}/unarchive`, {
      method: 'POST'
    });
  }

  // Students
  async getStudents(includeArchived = false) {
    const endpoint = includeArchived ? '/students?include_archived=true' : '/students';
    return this.request(endpoint);
  }

  async getStudentSuggestions() {
    return this.request('/students/suggestions');
  }

  async createStudent(studentData) {
    return this.request('/students', {
      method: 'POST',
      body: JSON.stringify(studentData)
    });
  }

  async updateStudent(studentId, studentData) {
    return this.request(`/students/${studentId}`, {
      method: 'PUT',
      body: JSON.stringify(studentData)
    });
  }

  async archiveStudent(studentId) {
    return this.request(`/students/${studentId}/archive`, {
      method: 'POST'
    });
  }

  async unarchiveStudent(studentId) {
    return this.request(`/students/${studentId}/unarchive`, {
      method: 'POST'
    });
  }

  // Invoices
  async getInvoicePreview(startDate, endDate, familyId = null) {
    let endpoint = `/invoices/preview?start_date=${startDate}&end_date=${endDate}`;
    if (familyId) {
      endpoint += `&family_id=${familyId}`;
    }
    return this.request(endpoint);
  }

  async saveInvoice(invoiceData) {
    return this.request('/invoices/save', {
      method: 'POST',
      body: JSON.stringify(invoiceData)
    });
  }

  async getInvoiceHistory(familyId = null) {
    let endpoint = '/invoices/history';
    if (familyId) {
      endpoint += `?family_id=${familyId}`;
    }
    return this.request(endpoint);
  }

  async getUnpaidSummary() {
    return this.request('/invoices/unpaid-summary');
  }

  async getRecentActivity(limit = 10) {
    return this.request(`/activity/recent?limit=${limit}`);
  }

  async getCodeQuestionReadiness() {
    return this.request('/game/code-questions/readiness');
  }

  async getCodeQuestions(filters, options = {}) {
    return this.request('/game/code-questions', {
      method: 'POST',
      signal: options.signal,
      body: JSON.stringify(filters),
    });
  }

  async getModelEditorAccess() {
    return this.request('/model-editor/access');
  }

  async getEditableModels(filters = {}) {
    const query = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') query.set(key, value);
    });
    return this.request(`/model-editor/models?${query.toString()}`);
  }

  async getEditableModel(modelId) {
    return this.request(`/model-editor/models/${encodeURIComponent(modelId)}`);
  }

  async getModelFacets(filters = {}) {
    const query = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') query.set(key, value);
    });
    return this.request(`/model-editor/facets?${query.toString()}`);
  }

  async searchModelSkills(search) {
    return this.request(`/model-editor/skills?${new URLSearchParams({ search: search || '' })}`);
  }

  async validateModel(body) {
    return this.request('/model-editor/validation', { method: 'POST', body: JSON.stringify(body) });
  }

  async updateModel(modelId, body) {
    return this.request(`/model-editor/models/${encodeURIComponent(modelId)}`, {
      method: 'PATCH', body: JSON.stringify(body),
    });
  }

  async createModel(body) {
    return this.request('/model-editor/models', { method: 'POST', body: JSON.stringify(body) });
  }

  async validateModelBatch(body) {
    return this.request('/model-editor/batch/validation', { method: 'POST', body: JSON.stringify(body) });
  }

  async saveModelBatch(body) {
    return this.request('/model-editor/batch', { method: 'POST', body: JSON.stringify(body) });
  }

  // ── Exam assignments (DEX1) ──
  async getExamCapacity(contentSpec) {
    return this.request('/exam-assignments/capacity', {
      method: 'POST', body: JSON.stringify(contentSpec),
    });
  }

  async listExamDrafts() {
    return this.request('/exam-assignments/exams');
  }

  async createExamDraft(draft) {
    return this.request('/exam-assignments/exams', {
      method: 'POST',
      body: JSON.stringify(draft),
    });
  }

  async getExamDraft(configurationId) {
    return this.request(`/exam-assignments/exams/${configurationId}`);
  }

  async saveExamDraft(configurationId, changes) {
    return this.request(`/exam-assignments/exams/${configurationId}`, {
      method: 'PATCH',
      body: JSON.stringify(changes),
    });
  }

  async buildExamContent(configurationId, contentSpec) {
    return this.request(`/exam-assignments/exams/${configurationId}/content`, {
      method: 'POST',
      body: JSON.stringify({ content_spec: contentSpec }),
    });
  }

  async regenerateExamItem(configurationId, payload) {
    return this.request(`/exam-assignments/exams/${configurationId}/content/regenerate`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async publishExamVersion(configurationId, payload) {
    return this.request(`/exam-assignments/exams/${configurationId}/versions`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async assignExam(payload) {
    return this.request('/exam-assignments/assignments', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async listExamAssignments() {
    return this.request('/exam-assignments/assignments');
  }

  async getExamAssignmentMonitor(assignmentId) {
    return this.request(`/exam-assignments/assignments/${assignmentId}/monitor`);
  }

  async reissueExamAccessLinks(assignmentId) {
    return this.request(`/exam-assignments/assignments/${assignmentId}/access-links`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async getExamAccessQr(assignmentId, accessToken) {
    return this.request(`/exam-assignments/assignments/${assignmentId}/access-qr`, {
      method: 'POST',
      body: JSON.stringify({ access_token: accessToken }),
    });
  }

  // ── Sitting an assigned exam (public link) ──
  async identifyExamRecipient(token, names) {
    return this.request(`/exam-access/${encodeURIComponent(token)}/identify`, {
      method: 'POST',
      body: JSON.stringify(names),
    });
  }

  // The link is public, so `session` — the signed proof of the declared name —
  // is what binds every call below to one pupil. It travels on all of them.
  async startExamAttempt(token, session) {
    return this.request(`/exam-access/${encodeURIComponent(token)}/attempt`, {
      method: 'POST',
      body: JSON.stringify({ ...session }),
    });
  }

  async saveExamAnswer(token, payload) {
    return this.request(`/exam-access/${encodeURIComponent(token)}/answer`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async examHeartbeat(token, attemptId, session) {
    return this.request(`/exam-access/${encodeURIComponent(token)}/heartbeat`, {
      method: 'POST',
      body: JSON.stringify({ ...session, attempt_id: attemptId }),
    });
  }

  async submitExamAttempt(token, attemptId, session) {
    return this.request(`/exam-access/${encodeURIComponent(token)}/submit`, {
      method: 'POST',
      body: JSON.stringify({ ...session, attempt_id: attemptId }),
    });
  }

  async getExamCorrection(token, attemptId, session) {
    // A GET carries the session in the query string.
    const query = new URLSearchParams(session || {}).toString();
    return this.request(
      `/exam-access/${encodeURIComponent(token)}/correction/${attemptId}?${query}`);
  }

  async getFamilyPresetStats(familyId) {
    return this.request(`/invoices/family-preset-stats?family_id=${familyId}`);
  }

  async getBulkPresetStats() {
    return this.request('/invoices/bulk-preset-stats');
  }

  async getEmailTargets(familyIds, presetMode, startDate, endDate, signal) {
    const base = `/invoices/email-targets?preset_mode=${presetMode}&start_date=${startDate}&end_date=${endDate}`;
    const suffix = familyIds === null
      ? '&all=true'
      : `&family_ids=${familyIds.join(',')}`;
    return this.request(base + suffix, signal ? { signal } : {});
  }

  async generateBulkPdfs(presetMode, startDate, endDate, familyIds) {
    return this.request('/invoices/generate-bulk-pdfs', {
      method: 'POST',
      body: JSON.stringify({
        preset_mode: presetMode,
        start_date:  startDate,
        end_date:    endDate,
        family_ids:  familyIds,
      }),
    });
  }

  async confirmBulkSend(jobs, { customSubject, customBody, isAnnual, annualYear } = {}) {
    return this.request('/invoices/confirm-bulk-send', {
      method: 'POST',
      body: JSON.stringify({
        jobs,
        custom_subject: customSubject || '',
        custom_body:    customBody    || '',
        is_annual:      isAnnual      || false,
        annual_year:    annualYear    || null,
      }),
    });
  }

  async sendPdfByEmail(filename, familyId, invoiceId, familyName, emails, { invoiceNumber, parentLastName, gender, customSubject, customBody, isAnnual, annualYear } = {}) {
    return this.request('/invoices/send-pdf-by-email', {
      method: 'POST',
      body: JSON.stringify({
        filename,
        family_id:        familyId,
        invoice_id:       invoiceId,
        family_name:      familyName,
        emails,
        invoice_number:   invoiceNumber   || '',
        parent_last_name: parentLastName  || '',
        gender:           gender          || '',
        custom_subject:   customSubject   || '',
        custom_body:      customBody      || '',
        is_annual:        isAnnual        || false,
        annual_year:      annualYear      || null,
      }),
    });
  }

  async generateFiscalAttestation(familyId, year) {
    const url = `${this.getApiBase()}/invoices/fiscal-attestation?family_id=${familyId}&year=${year}`;
    const resp = await serverRequest(url, { credentials: 'include' });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.error || 'Erreur lors de la génération de l\'attestation');
    }
    const blob = await resp.blob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Attestation_SAP_${year}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
  }

  async generatePDF(invoiceData) {
    return this.request('/invoices/generate-pdf', {
      method: 'POST',
      body: JSON.stringify(invoiceData)
    });
  }

  async downloadPDF(filename) {
    const url = `${this.getApiBase()}/invoices/download/${filename}`;

    try {
      const response = await serverRequest(url, {
        method: 'GET',
        credentials: 'include',
        headers: {}
      });

      if (!response.ok) {
        throw new Error(`Failed to download PDF: ${response.statusText}`);
      }

      // Get the blob from the response
      const blob = await response.blob();

      // Create a temporary URL for the blob
      const blobUrl = window.URL.createObjectURL(blob);

      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      throw error;
    }
  }

  // Payments
  async getPayments(filters = {}) {
    const params = new URLSearchParams(filters).toString();
    return this.request(`/payments${params ? '?' + params : ''}`);
  }

  async createPayment(paymentData) {
    return this.request('/payments', {
      method: 'POST',
      body: JSON.stringify(paymentData)
    });
  }

  async updatePayment(paymentId, fields) {
    return this.request(`/payments/${paymentId}`, {
      method: 'PUT',
      body: JSON.stringify(fields)
    });
  }

  async cancelPayment(paymentId, reason) {
    return this.request(`/payments/${paymentId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason })
    });
  }

  async getPaymentStats(startDate = null, endDate = null) {
    let endpoint = '/payments/stats';
    if (startDate && endDate) {
      endpoint += `?start_date=${startDate}&end_date=${endDate}`;
    }
    return this.request(endpoint);
  }

  async getPaymentMethods() {
    return this.request('/payment-methods');
  }

  async getFamilyPaymentInfo(familyId) {
    return this.request(`/payments/family/${familyId}/info`);
  }

  // MOB1 record pages: newest-first feed (kind repeatable) + vitals/alert
  async getRecordTimeline(familyId, { student_id, kind, cursor } = {}) {
    const params = new URLSearchParams();
    if (student_id) params.set('student_id', student_id);
    (kind || []).forEach((k) => params.append('kind', k));
    if (cursor) params.set('cursor', cursor);
    const query = params.toString();
    return this.request(`/families/${familyId}/timeline${query ? `?${query}` : ''}`);
  }

  async getRecordSummary(familyId, studentId) {
    return this.request(`/families/${familyId}/summary${studentId ? `?student_id=${studentId}` : ''}`);
  }

  /** REC1: `expected` = the summary's `next_session` the teacher saw (stale → 409). */
  skipNextSession(studentId, expected) {
    return this.request(`/students/${studentId}/next-session/skip`, { method: 'POST', body: JSON.stringify({ expected }) });
  }

  /** Agenda menu: cancel one date of a slot (YYYY-MM-DD); its other dates stay. */
  skipScheduleOccurrence(scheduleId, date) {
    return this.request(`/lesson-schedules/${scheduleId}/occurrences/skip`, { method: 'POST', body: JSON.stringify({ date }) });
  }

  moveNextSession(studentId, expected, date, startTime) {
    return this.request(`/students/${studentId}/next-session/move`, {
      method: 'POST', body: JSON.stringify({ expected, date, start_time: startTime }),
    });
  }

  // Statistics
  async getStatisticsWorkspace() {
    return this.request('/statistics/workspace');
  }

  async getStatisticsOverview() {
    return this.request('/statistics/overview');
  }

  async getStatisticsPeriod(params = {}) {
    const query = new URLSearchParams(Object.entries(params).filter(([, value]) => value != null && value !== '')).toString();
    return this.request(`/statistics/period${query ? `?${query}` : ''}`);
  }

  async getStatisticsDeclarationSummary() {
    return this.request('/statistics/declaration-summary');
  }

  async getStatisticsDeclaration(params = {}) {
    const query = new URLSearchParams(Object.entries(params).filter(([, value]) => value != null && value !== '')).toString();
    return this.request(`/statistics/declaration${query ? `?${query}` : ''}`);
  }

  isReminderDismissed(key) { return this.get(`/smart-actions/dismissed?key=${encodeURIComponent(key)}`); }

  dismissReminder(key) { return this.post('/smart-actions/dismiss', { action_key: key }); }

  async saveDeclarationFilters(filters) {
    return this.request('/statistics/declaration-filters', { method: 'PUT', body: JSON.stringify(filters) });
  }

  async getStatisticsHistory(params = {}) {
    const query = new URLSearchParams(Object.entries(params).filter(([, value]) => value != null && value !== '')).toString();
    return this.request(`/statistics/history${query ? `?${query}` : ''}`);
  }

  async getStatistics(options = {}) {
    return this.request('/statistics', options);
  }

  async getLifetimeHours() {
    return this.request(STATISTICS.LIFETIME_HOURS);
  }

  async getRevenueStats() {
    return this.request('/statistics/revenue');
  }

  async getStudentStats(params = {}) {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
    ).toString();
    return this.request(`/statistics/students${qs ? `?${qs}` : ''}`);
  }

  async getStudentDetail(studentId) {
    return this.request(`/statistics/student-detail/${studentId}`);
  }

  async getPaymentMethodStats(params = {}) {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
    ).toString();
    return this.request(`/statistics/payment-methods${qs ? `?${qs}` : ''}`);
  }

  async getPaymentsLog(params = {}) {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
    ).toString();
    return this.request(`/statistics/payments-log${qs ? `?${qs}` : ''}`);
  }

  async getLessonsLog(params = {}) {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
    ).toString();
    return this.request(`/statistics/lessons-log${qs ? `?${qs}` : ''}`);
  }

  async getPromos() {
    return this.request('/reports/promos');
  }

  async getPendingPayments() {
    return this.request('/payments/pending');
  }

  async getUpcomingLessons(limit = 10) {
    return this.request(`/lessons/upcoming?limit=${limit}`);
  }

  // URSSAF — sync payment statuses for the authenticated teacher
  async syncUrssafPaymentStatuses() {
    return this.request('/urssaf/sync-payment-statuses', { method: 'POST' });
  }

  // URSSAF — all payment demands for the teacher (for Statistics log table)
  async urssafGetPaymentDemands() {
    return this.request('/urssaf/payment-demands');
  }

  // Security - Password verification for sensitive operations
  async verifyPassword(password) {
    return this.request('/auth/verify-password', {
      method: 'POST',
      body: JSON.stringify({ password })
    });
  }

  // Configuration & Messages
  async getConfig() {
    return this.request('/config');
  }

  async getMessages() {
    return this.request('/messages');
  }

  // Reference data
  async getClassLevels() {
    return this.request('/reference/class-levels');
  }

  // Lessons
  async getLessons() {
    return this.request('/lessons');
  }

  async getLesson(lessonId) {
    return this.request(`/lessons/${lessonId}`);
  }

  async createLesson(lessonData) {
    return this.request('/lessons', {
      method: 'POST',
      body: JSON.stringify(lessonData)
    });
  }

  async updateLesson(lessonId, lessonData) {
    return this.request(`/lessons/${lessonId}`, {
      method: 'PUT',
      body: JSON.stringify(lessonData)
    });
  }

  async deleteLesson(lessonId) {
    return this.request(`/lessons/${lessonId}`, {
      method: 'DELETE'
    });
  }

  async cancelLesson(lessonId) {
    return this.request(`/lessons/${lessonId}/cancel`, {
      method: 'POST'
    });
  }

  async getStudentLessons(studentId) {
    return this.request(`/lessons/student/${studentId}`);
  }

  /** Teacher record: the family-space mission cards `{groups, counts, subjects, next_offset}`, same filters. */
  getStudentMissions(studentId, { q = '', from = '', to = '', subject = '', status = MISSION_STATUS_FILTER.ALL, offset = 0 } = {}) {
    return this.request(`/students/${studentId}/missions?${new URLSearchParams({ q, from, to, subject, status, offset })}`);
  }

  /** REC2 missions tool: every student's mission cards (`student_id` '' = all). */
  getMissions({ student_id = '', q = '', from = '', to = '', subject = '', status = MISSION_STATUS_FILTER.ALL, offset = 0 } = {}) {
    return this.request(`/missions?${new URLSearchParams({ student_id, q, from, to, subject, status, offset })}`);
  }

  async getLessonsByDateRange(startDate, endDate) {
    return this.request(`/lessons/date-range?start_date=${startDate}&end_date=${endDate}`);
  }

  // Schedules
  async getSchedules() {
    return this.request('/lesson-schedules');
  }

  async getSchedule(scheduleId) {
    return this.request(`/lesson-schedules/${scheduleId}`);
  }

  async createSchedule(scheduleData) {
    return this.request('/lesson-schedules', {
      method: 'POST',
      body: JSON.stringify(scheduleData)
    });
  }

  async updateSchedule(scheduleId, scheduleData) {
    return this.request(`/lesson-schedules/${scheduleId}`, {
      method: 'PUT',
      body: JSON.stringify(scheduleData)
    });
  }

  async deleteSchedule(scheduleId) {
    return this.request(`/lesson-schedules/${scheduleId}`, {
      method: 'DELETE'
    });
  }

  async getStudentSchedules(studentId) {
    return this.request(`/lesson-schedules/student/${studentId}`);
  }

  async getScheduleConflicts() {
    return this.request('/lesson-schedules/conflicts');
  }

  async getCalendarEvents(start, end, zones = []) {
    const query = new URLSearchParams({ start, end });
    if (zones.length) query.set('zones', zones.join(','));
    return this.request(`/calendar/events?${query.toString()}`);
  }

  async createCalendarEvent(eventData) {
    return this.request('/calendar/events', { method: 'POST', body: JSON.stringify(eventData) });
  }

  async updateCalendarEvent(eventId, eventData) {
    return this.request(`/calendar/events/${eventId}`, { method: 'PUT', body: JSON.stringify(eventData) });
  }

  async deleteCalendarEvent(eventId) {
    return this.request(`/calendar/events/${eventId}`, { method: 'DELETE' });
  }

  // Tasks
  async getTasks() {
    return this.request('/tasks');
  }

  async getTask(taskId) {
    return this.request(`/tasks/${taskId}`);
  }

  async createTask(taskData) {
    return this.request('/tasks', {
      method: 'POST',
      body: JSON.stringify(taskData)
    });
  }

  async updateTask(taskId, taskData) {
    return this.request(`/tasks/${taskId}`, {
      method: 'PUT',
      body: JSON.stringify(taskData)
    });
  }

  // Curriculum tagging (G2) — "Quel chapitre ?"
  async suggestTaskTopic(text, classLevel) {
    return this.request('/tasks/suggest-topic', {
      method: 'POST',
      body: JSON.stringify({ text, class_level: classLevel })
    });
  }

  // `studentId` lets the server read the PREVIOUS lessons too — the last
  // session's missions and this school year's older ones (T-DEFI2.19 §3).
  async suggestLessonTopics(context, classLevel, studentId = null) {
    return this.request('/tasks/suggest-lesson-topics', {
      method: 'POST',
      body: JSON.stringify({ context, class_level: classLevel, student_id: studentId })
    });
  }

  async confirmTaskTopic(taskId, topicTag, learnToken = null) {
    return this.request(`/tasks/${taskId}/topic`, {
      method: 'POST',
      body: JSON.stringify({ topic_tag: topicTag, learn_token: learnToken })
    });
  }

  // Public practice links (G2 / stage B5)
  async createGameLink({ studentId, topicTag, family = 'calcul', label = null, lessonId = null }) {
    return this.request('/games/share', {
      method: 'POST',
      body: JSON.stringify({
        student_id: studentId,
        topic_tag: topicTag,
        family,
        label,
        lesson_id: lessonId,
      })
    });
  }

  // ── PUBLIC — no session, no CSRF token ──
  // These two are reached by a student with no account, so they must never be
  // routed through the authenticated request helper.
  async getSharedGame(linkId) {
    const res = await serverRequest(`${this.getApiBase()}/games/shared/${linkId}`, {
      headers: { 'Content-Type': 'application/json' },
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const error = new Error(body?.message || 'link error');
      error.status = res.status;
      throw error;
    }
    return body;
  }

  async recordSharedGameResult(linkId, payload) {
    const res = await serverRequest(`${this.getApiBase()}/games/shared/${linkId}/result`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const error = new Error(body?.message || 'link error');
      error.status = res.status;
      throw error;
    }
    return body;
  }

  // ── Placement test (G2 / stage B6) ──
  // `checkPlacement` is what the questions step branches on: on a first lesson
  // (ever, or of a new school year) the history questions have no history to
  // ask about, so they are suppressed and the test is offered instead.
  async checkPlacement(studentId) {
    return this.request(PLACEMENT.CHECK(studentId));
  }

  async getPlacementTest(studentId, subject = null) {
    return this.request(PLACEMENT.TEST(studentId, subject));
  }

  async setPlacementSchoolType(studentId, schoolType) {
    return this.request(PLACEMENT.SCHOOL_TYPE, {
      method: 'POST',
      body: JSON.stringify({ student_id: studentId, school_type: schoolType })
    });
  }

  async submitPlacementTest({ studentId, subject, answers }) {
    return this.request(PLACEMENT.SUBMIT, {
      method: 'POST',
      body: JSON.stringify({ student_id: studentId, subject, answers })
    });
  }

  // ── PUBLIC — no session, no CSRF token ──
  // `/test/:subject/:classLevel` is open to anyone, so it must never be routed
  // through the authenticated request helper. Nothing is stored and nothing
  // private comes back: the class is in the URL and the questions come from
  // the public curriculum bank.
  async publicRequest(endpoint, payload = undefined, { method = '', token = '', keepalive = false, signal } = {}) {
    const res = await serverRequest(`${this.getApiBase()}${endpoint}`, {
      method: method || (payload === undefined ? 'GET' : 'POST'),
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      ...(payload === undefined ? {} : { body: JSON.stringify(payload) }),
      ...(keepalive ? { keepalive } : {}),
      ...(signal ? { signal } : {}),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const error = new Error(body?.message || 'public request error');
      error.status = res.status;
      throw error;
    }
    return body;
  }

  async getPublicTest(subject, classLevel) {
    return this.publicRequest(PLACEMENT.PUBLIC_TEST(subject, classLevel));
  }

  async advancePublicDiagnostic(subject, classLevel, payload) {
    return this.publicRequest(PLACEMENT.PUBLIC_DIAGNOSTIC(subject, classLevel), payload);
  }

  // ── PUBLIC calcul drill (T-CALC1) — nothing stored ──
  async getCalculCatalogue() {
    return this.publicRequest(CALCUL.CATALOGUE);
  }

  /** `freeText`: a sentence a teacher wrote — maths notation inside it is ignored. */
  async searchCalcul(query, { freeText = false } = {}) {
    return this.publicRequest(CALCUL.SEARCH(query, freeText));
  }

  async createCalculSession(payload, options) {
    return this.publicRequest(CALCUL.SESSION, payload, options);
  }

  // ── Calcul setup wizard (CALC6) ──
  async getCalculSkillSchema() {
    return this.publicRequest(CALCUL.SKILL_SCHEMA);
  }

  async createCalculSkillSession(payload, options) {
    return this.publicRequest(CALCUL.SKILL_SESSION, payload, options);
  }

  async getCalculQr(path) {
    return this.publicRequest(CALCUL.QR(path));
  }

  async getNumberWordsSession(params, options) {
    return this.publicRequest(CALCUL.NUMBER_WORDS_SESSION(params), undefined, options);
  }

  async checkNumberWords(payload, options) {
    return this.publicRequest(CALCUL.NUMBER_WORDS_CHECK, payload, options);
  }

  async getPublicNotionCatalogue(classLevel) {
    return this.publicRequest(NOTION_PRACTICE.CATALOGUE(classLevel));
  }

  async createNotionPracticeSession(payload, options) {
    return this.publicRequest(NOTION_PRACTICE.SESSION, payload, options);
  }

  // ── Mission drafter (authenticated teacher authoring) ──
  async draftMissions(payload) {
    return this.request(MISSION_DRAFTS.DRAFT, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // ── National exams (PUBLIC, stateless) ──
  async getNationalExamCatalogue() {
    return this.publicRequest(NATIONAL_EXAMS.CATALOGUE);
  }

  async gradeExamPractice(payload, options) {
    return this.publicRequest(NATIONAL_EXAMS.PRACTICE_GRADE, payload, options);
  }

  async getNationalExamSubject(exam, year, session, seed = '') {
    const route = NATIONAL_EXAMS.SUBJECT(exam, year, session);
    return this.publicRequest(seed ? `${route}?seed=${encodeURIComponent(seed)}` : route);
  }

  async gradeNationalExam(exam, year, session, payload) {
    return this.publicRequest(NATIONAL_EXAMS.GRADE(exam, year, session), payload);
  }

  // ── Examination Chamber (PUBLIC, anonymous persisted sessions) ──
  async getExamSubjects() {
    return this.publicRequest(NATIONAL_EXAMS.SUBJECTS);
  }

  async createExamSession(payload, replayToken = '') {
    return this.publicRequest(NATIONAL_EXAMS.SESSION, payload, { token: replayToken });
  }

  async getExamSession(sessionId, token) {
    return this.publicRequest(NATIONAL_EXAMS.SESSION_BY_ID(sessionId), undefined, { token });
  }

  async saveExamCheckpoint(sessionId, token, payload, { keepalive = false } = {}) {
    return this.publicRequest(NATIONAL_EXAMS.SESSION_BY_ID(sessionId), payload, { method: 'PATCH', token, keepalive });
  }

  async submitExamSession(token, payload) {
    return this.publicRequest(NATIONAL_EXAMS.RESULT, payload, { token });
  }

  async getExamResult(resultId, token) {
    return this.publicRequest(NATIONAL_EXAMS.RESULT_BY_ID(resultId), undefined, { token });
  }

  async downloadNationalExamPdf(exam, year, session, payload) {
    const response = await serverRequest(`${this.getApiBase()}${NATIONAL_EXAMS.PDF(exam, year, session)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const error = new Error(body?.message || 'PDF indisponible');
      error.status = response.status;
      throw error;
    }
    return response.blob();
  }

  async deleteTask(taskId) {
    return this.request(`/tasks/${taskId}`, {
      method: 'DELETE'
    });
  }

  async getLessonTasks(lessonId) {
    return this.request(`/tasks/lesson/${lessonId}`);
  }

  // Evaluations
  async getEvaluations() {
    return this.request('/evaluations');
  }

  async getEvaluation(evaluationId) {
    return this.request(`/evaluations/${evaluationId}`);
  }

  async createEvaluation(evaluationData) {
    return this.request('/evaluations', {
      method: 'POST',
      body: JSON.stringify(evaluationData)
    });
  }

  async updateEvaluation(evaluationId, evaluationData) {
    return this.request(`/evaluations/${evaluationId}`, {
      method: 'PUT',
      body: JSON.stringify(evaluationData)
    });
  }

  async deleteEvaluation(evaluationId) {
    return this.request(`/evaluations/${evaluationId}`, {
      method: 'DELETE'
    });
  }

  async getStudentEvaluations(studentId, subject = null) {
    let endpoint = `/evaluations/student/${studentId}`;
    if (subject) {
      endpoint += `?subject=${subject}`;
    }
    return this.request(endpoint);
  }

  async getStudentEvaluationStats(studentId) {
    return this.request(`/evaluations/stats/${studentId}`);
  }

  // Homework
  async getHomework() {
    return this.request('/homework');
  }

  async getHomeworkAssignment(assignmentId) {
    return this.request(`/homework/${assignmentId}`);
  }

  async createHomework(homeworkData) {
    return this.request('/homework', {
      method: 'POST',
      body: JSON.stringify(homeworkData)
    });
  }

  async updateHomework(assignmentId, homeworkData) {
    return this.request(`/homework/${assignmentId}`, {
      method: 'PUT',
      body: JSON.stringify(homeworkData)
    });
  }

  async deleteHomework(assignmentId) {
    return this.request(`/homework/${assignmentId}`, {
      method: 'DELETE'
    });
  }

  async getStudentHomework(studentId, completed = null) {
    let endpoint = `/homework/student/${studentId}`;
    if (completed !== null) {
      endpoint += `?completed=${completed}`;
    }
    return this.request(endpoint);
  }

  // System
  async createBackup() {
    return this.request('/backup', {
      method: 'POST'
    });
  }

  async getFamily(familyId) {
    return this.request(`/families/${familyId}`);
  }

  // ── Course Sessions (multi-tab safe; see migration 039) ─────────────────
  //
  // Sessions are addressed by session_id. The client supplies a tab_id at
  // create time and an expected_version on every update so two tabs/devices
  // editing the same session can detect each other (409) instead of silently
  // overwriting.

  async listActiveCourseSessions() {
    return this.request('/course-sessions');
  }

  async getCourseSession(sessionId) {
    return this.request(`/course-sessions/${sessionId}`);
  }

  async getCourseSessionDraftContext(studentId) {
    return this.request(`/course-sessions/draft-context/${studentId}`);
  }

  async updatePreviousLessonMissions(lessonId, missions) {
    return this.request(`/course-sessions/lessons/${lessonId}/missions`, {
      method: 'PATCH',
      body: JSON.stringify({ missions })
    });
  }

  async shareMissions(payload) {
    return this.request('/missions/share', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  // FAM2 R4: teacher answer about the previous lesson's missions.
  getPreviousMissionStatus(studentId, before, excludeLessonId = null) {
    const params = new URLSearchParams({ student_id: studentId, before });
    if (excludeLessonId) params.set('exclude_lesson_id', excludeLessonId);
    return this.request(`${MISSION_COMPLETION_API.PREVIOUS}?${params}`);
  }

  setMissionTeacherAnswer(lessonId, { done, difficulty = null }) {
    return this.request(MISSION_COMPLETION_API.ANSWER(lessonId), {
      method: 'PUT',
      body: JSON.stringify({ done, difficulty }),
    });
  }

  async createCourseSession(sessionData) {
    return this.request('/course-sessions', {
      method: 'POST',
      body: JSON.stringify(sessionData)
    });
  }

  async updateCourseSession(sessionId, sessionData) {
    return this.request(`/course-sessions/${sessionId}`, {
      method: 'PUT',
      body: JSON.stringify(sessionData)
    });
  }

  async completeCourseSession(sessionId) {
    return this.request(`/course-sessions/${sessionId}/complete`, {
      method: 'POST'
    });
  }

  async cancelCourseSession(sessionId) {
    return this.request(`/course-sessions/${sessionId}/cancel`, {
      method: 'POST'
    });
  }

  // Game
  // History is now managed in frontend localStorage
  // Backend only needs history_ids to exclude and spins_since_arm_wrestling for boost
  async getRandomActivity(
    preferredActivityType,
    historyIds = [],
    spinsSinceFamily = {},
    studentId = null,
    classLevel = null,
    topicTag = null,
    topicMode = 'auto',
    contextTopics = [],
    // The défi's tile grid: 'maths' | 'calcul' | 'sport' | 'mental' |
    // 'english' | 'autre'. ⚠ An INTENT, never a family id — the server maps it,
    // so `sport_a` / `sport_b` never exist as strings on this side.
    pick = null,
    // The notion run (T-DEFI2.19): `{ ref, origin, class_level, state, outcomes }`.
    // `ref` null = the system chooses; `state` is the run's progress, carried
    // here and re-validated by the server on every call.
    notion = null,
      drawState = null,
      sessionMastery = null,
      startLevel = 1,
      options = {}
  ) {
    return this.request('/game/random-activity', {
      method: 'POST',
      signal: options.signal,
      body: JSON.stringify({
        preferred_activity_type: preferredActivityType,
        history_ids: historyIds,
        spins_since_family: spinsSinceFamily,
        student_id: studentId,
        class_level: classLevel,
        topic_tag: topicTag,
        topic_mode: topicMode,
        context_topics: contextTopics,
        pick,
        english: options.english,
        notion,
        draw_state: drawState,
        start_level: startLevel,
        ...(sessionMastery !== null ? { session_mastery: sessionMastery } : {})
      })
    });
  }

  async buildExam(payload) {
    return this.request('/exams/build', {
      method: 'POST',
      readOnly: true, // computes an exam, saves nothing: allowed in the guide sandbox
      body: JSON.stringify(payload),
    });
  }

  // Adaptive game (G2) — one row per challenge, plus the mastery move.
  async recordGameResult(payload) {
    return this.request('/game/result', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  async getStudentMastery(studentId) {
    return this.request(`/game/mastery/${studentId}`);
  }

  // Game settings rows (model_type 'game', edited in /models): one per class band and level.
  async getGamePlaySettings(game, classLevel, level) {
    const params = new URLSearchParams({ class_level: classLevel || '', level: String(level || 1) });
    return this.request(`/game/play-settings/${encodeURIComponent(game)}?${params}`);
  }

  async getAllActivities() {
    return this.request('/game/activities');
  }

  async getAllActivitiesFull() {
    return this.request('/game/activities/full');
  }

  async addActivity(activityData) {
    return this.request('/game/activities', {
      method: 'POST',
      body: JSON.stringify(activityData)
    });
  }

  async updateActivity(activityId, activityData) {
    return this.request(`/game/activities/${activityId}`, {
      method: 'PUT',
      body: JSON.stringify(activityData)
    });
  }

  async deleteActivity(activityId) {
    return this.request(`/game/activities/${activityId}`, {
      method: 'DELETE'
    });
  }

  async resetActivities() {
    return this.request('/game/activities/reset', {
      method: 'POST'
    });
  }

  async importActivitiesCSV(formData) {
    const url = `${this.getApiBase()}/game/activities/import`;
    const response = await serverRequest(url, {
      method: 'POST',
      credentials: 'include',
      body: formData
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Import failed');
    }
    return data;
  }

  // Academic History
  async getStudentAcademicHistory(studentId) {
    return this.request(`/academic-history/student/${studentId}`);
  }

  async getAcademicHistoryDefaults(studentId) {
    return this.request(`/academic-history/defaults/${studentId}`);
  }

  async createAcademicHistory(historyData) {
    return this.request('/academic-history', {
      method: 'POST',
      body: JSON.stringify(historyData)
    });
  }

  async updateAcademicHistory(historyId, historyData) {
    return this.request(`/academic-history/${historyId}`, {
      method: 'PUT',
      body: JSON.stringify(historyData)
    });
  }

  async deleteAcademicHistory(historyId) {
    return this.request(`/academic-history/${historyId}`, {
      method: 'DELETE'
    });
  }

  async getAllAcademicGrades() {
    return this.request('/academic-history/all-grades');
  }

  // URSSAF / Home+ Avance Immédiate
  async urssafCredentialsStatus() {
    return this.request('/urssaf/credentials/status');
  }

  async urssafSaveCredentials(clientId, clientSecret) {
    return this.request('/urssaf/credentials', {
      method: 'POST',
      body: JSON.stringify({ client_id: clientId, client_secret: clientSecret }),
    });
  }

  async urssafTestConnection() {
    return this.request('/urssaf/credentials/test', { method: 'POST' });
  }

  // URS2: unpaidViaUrssaf = the teacher chose URSSAF for the unpaid lessons (server re-checks).
  async urssafPrepareRegister(familyId, { unpaidViaUrssaf = false } = {}) {
    return this.request(`/urssaf/families/${familyId}/register/prepare`, {
      method: 'POST',
      body: JSON.stringify({ unpaid_via_urssaf: unpaidViaUrssaf }),
    });
  }

  async urssafRegisterFamily(familyId, confirmationToken, { unpaidViaUrssaf = false } = {}) {
    return this.request(`/urssaf/families/${familyId}/register`, {
      method: 'POST',
      body: JSON.stringify({ confirmation_token: confirmationToken, unpaid_via_urssaf: unpaidViaUrssaf }),
    });
  }

  // URS2: what to offer when the family owes money (mode none|pay_other_way|choose|proposals).
  async urssafRegistrationBalance(familyId) {
    return this.request(`/urssaf/families/${familyId}/register/balance`);
  }

  async urssafResetRegistration(familyId) {
    return this.request(`/urssaf/families/${familyId}/registration`, { method: 'DELETE' });
  }

  async urssafRestoreRegistration(familyId) {
    return this.request(`/urssaf/families/${familyId}/registration/restore`, { method: 'POST' });
  }

  async urssafFamilyStatus(familyId) {
    return this.request(`/urssaf/families/${familyId}/status`);
  }

  // URS1: GET /particulier by idClient, stored server-side (throttled).
  async urssafRefreshStatus(familyId) {
    return this.request(`/urssaf/families/${familyId}/status/refresh`, { method: 'POST' });
  }

  // URS1 « Plus tard »: teacher + family reminders.
  async urssafRemindLater(familyId) {
    return this.request(`/urssaf/families/${familyId}/status/remind-later`, { method: 'POST' });
  }

  // URS1 Option A: attested lesson date correction.
  // URS2 « Réessayer demain »: teacher feed row now, inbox action tomorrow 08:00.
  async urssafScheduleDemandRetry(familyId, lessonIds, kind = 'demand') {
    return this.request(`/urssaf/families/${familyId}/demand-retry`, {
      method: 'POST',
      body: JSON.stringify({ lesson_ids: lessonIds, kind }),
    });
  }

  async urssafAdjustLessonDate(lessonId, lessonDate, startTime) {
    return this.request(`/urssaf/lessons/${lessonId}/adjust-date`, {
      method: 'POST',
      body: JSON.stringify({ lesson_date: lessonDate, start_time: startTime, attestation: true }),
    });
  }

  // GET /payments/families/<id>/unpaid-lessons
  async getUnpaidLessons(familyId) {
    return this.request(`/payments/families/${familyId}/unpaid-lessons`);
  }

  // POST /urssaf/payment-demand/prepare (lesson-list flow — creates invoice)
  async urssafPreparePdemandFromLessons(familyId, lessons, demandDatetime) {
    return this.request('/urssaf/payment-demand/prepare', {
      method: 'POST',
      body: JSON.stringify({
        family_id: familyId,
        demand_datetime: demandDatetime,
        lessons,
      }),
    });
  }

  // POST /urssaf/invoices/<id>/payment-demand/prepare (invoice-flow — invoice already exists)
  async urssafPreparePdemandByInvoice(invoiceId) {
    return this.request(`/urssaf/invoices/${invoiceId}/payment-demand/prepare`, {
      method: 'POST',
    });
  }

  // POST /urssaf/invoices/<id>/payment-demand (confirm — shared by both flows)
  async urssafConfirmPdemand(invoiceId, confirmationToken) {
    return this.request(`/urssaf/invoices/${invoiceId}/payment-demand`, {
      method: 'POST',
      body: JSON.stringify({ confirmation_token: confirmationToken }),
    });
  }

  // ── Family space (PWA1) — device cookie, never the teacher session ───────

  familyRequest(endpoint, options = {}) {
    return this.request(endpoint, { ...options, skipSessionHandling: true });
  }

  familyPost(endpoint, body = {}) {
    return this.familyRequest(endpoint, { method: 'POST', body: JSON.stringify(body) });
  }

  // PDFs (statement, yearly invoice, issued invoice) come back as a Blob.
  async familyBlob(endpoint, { method = 'GET', body = null } = {}) {
    return serverRequest(`${this.getApiBase()}${endpoint}`, {
      method,
      interactive: true,
      credentials: 'include',
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    }, async (response) => {
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      const error = new Error(data.message || data.error || `HTTP ${response.status}`);
      error.status = response.status;
      throw error;
    }
    return response.blob();
    });
  }

  familyIcsFeedUrl(token) {
    return `${this.getApiBase()}${FAMILY_API.ICS_FEED}?token=${encodeURIComponent(token)}`;
  }

  getFamilyBootstrap() { return this.familyRequest(FAMILY_API.BOOTSTRAP); }
  redeemFamilyCode(code) { return this.familyPost(FAMILY_API.REDEEM, { code }); }
  refreshFamilyDevice(identity) { return this.familyPost(FAMILY_API.REFRESH, identity); }
  getFamilyRefreshMode() { return this.familyRequest(FAMILY_API.REFRESH_MODE); }
  getFamilyIdentityMode() { return this.familyRequest(FAMILY_API.IDENTITY_MODE); }
  setFamilyMemoDate(memoDate) {
    return this.familyRequest(FAMILY_API.MEMO_DATE, { method: 'PUT', body: JSON.stringify({ memo_date: memoDate }) });
  }
  revokeFamilyDevice(token) { return this.familyPost(FAMILY_API.REVOKE, { token }); }
  postFamilyLogin(actorKind, studentId = null) {
    return this.familyPost(FAMILY_API.LOGINS, { actor_kind: actorKind, student_id: studentId });
  }
  getFamilyDeviceLogins(deviceId) { return this.familyRequest(FAMILY_API.DEVICE_LOGINS(deviceId)); }
  getFamilyHome() { return this.familyRequest(FAMILY_API.HOME); }
  getFamilyTiles() { return this.familyRequest(FAMILY_API.TILES); }
  getFamilyMissions(studentId, { q = '', from = '', to = '', subject = '', status = MISSION_STATUS_FILTER.ALL, offset = 0 } = {}) {
    const params = new URLSearchParams({ student_id: studentId, q, from, to, subject, status, offset });
    return this.familyRequest(`${FAMILY_API.MISSIONS}?${params}`, { interactive: offset > 0 });
  }
  setFamilyMissionDone(lessonId, studentId, done) {
    return this.familyRequest(FAMILY_API.MISSION_DONE(lessonId), {
      method: 'PUT', body: JSON.stringify({ student_id: studentId, done }),
    });
  }
  requestFamilyDataDeletion() { return this.familyPost(FAMILY_API.DATA_DELETION); }
  // FAM3 R8 history: `q` = French month | amount | subject (lessons only); backend `limit` default 5.
  familyQuery(endpoint, params) {
    const query = new URLSearchParams(Object.entries(params).filter(([, value]) => value != null && value !== ''));
    return this.familyRequest(query.toString() ? `${endpoint}?${query}` : endpoint);
  }
  getFamilyLessons({ studentId = null, q = '', limit = null } = {}) {
    return this.familyQuery(FAMILY_API.LESSONS, { student_id: studentId, q, limit });
  }
  getFamilyAgenda() { return this.familyRequest(FAMILY_API.AGENDA); }
  getFamilyPayments({ q = '', limit = null } = {}) { return this.familyQuery(FAMILY_API.PAYMENTS, { q, limit }); }
  getFamilyProgress(studentId, { offset = 0 } = {}) {
    return this.familyRequest(`${FAMILY_API.PROGRESS}?student_id=${studentId}&offset=${offset}`, { interactive: offset > 0 });
  }
  createFamilyStatement(preset) { return this.familyBlob(FAMILY_API.STATEMENTS, { method: 'POST', body: { preset } }); }
  createFamilyYearlyInvoice() { return this.familyBlob(FAMILY_API.YEARLY_INVOICE, { method: 'POST', body: {} }); }
  createFamilyAttestation() { return this.familyBlob(FAMILY_API.ATTESTATION, { method: 'POST', body: {} }); }
  createFamilyScheduleRequest(payload) { return this.familyPost(FAMILY_API.SCHEDULE_REQUESTS, payload); }
  createFamilyIcsToken() { return this.familyPost(FAMILY_API.ICS_TOKEN); }
  getFamilyDevices() { return this.familyRequest(FAMILY_API.DEVICES); }
  // NOTIF1 T26: {items[{…, browser}], next_cursor, unread}; read `upTo` = newest cursor shown.
  getFamilyNotifications(cursor) {
    return this.familyRequest(cursor ? `${FAMILY_API.NOTIFICATIONS}?cursor=${encodeURIComponent(cursor)}` : FAMILY_API.NOTIFICATIONS);
  }
  markFamilyNotificationsRead(upTo) { return this.familyPost(FAMILY_API.NOTIFICATIONS_READ, { up_to: upTo }); }
  signalFamilyDevice(deviceId) { return this.familyPost(FAMILY_API.SIGNAL(deviceId)); }
  createFamilyShareCode() { return this.familyPost(FAMILY_API.SHARE_CODE); }
  getFamilyAiPreview() { return this.familyRequest(FAMILY_API.AI_PREVIEW); }
  // FAM3 R11: `actor` = {actor_kind, student_id?} from the payer widget.
  // FAM3-B: direct send to URSSAF. `payload` = identity fields + children_count (when the backend asked).
  sendFamilyAvanceImmediate(payload, actor) { return this.familyPost(FAMILY_API.AI_REQUESTS, { ...payload, ...actor }); }

  // Teacher side (JWT session, normal 401 handling)
  createFamilyInvite(familyId) { return this.post(FAMILY_API.TEACHER_INVITES, { family_id: familyId }); }
  getFamilyAccess(familyId) { return this.request(`${FAMILY_API.TEACHER_DEVICES}?family_id=${familyId}`); }
  revokeFamilyDeviceAsTeacher(deviceId) { return this.post(FAMILY_API.TEACHER_REVOKE_DEVICE(deviceId), {}); }
  revokeAllFamilyDevices(familyId) { return this.post(FAMILY_API.TEACHER_REVOKE_ALL(familyId), {}); }
  getFamilyAiRequests() { return this.request(FAMILY_API.TEACHER_AI_REQUESTS); }
  resolveFamilyAiRequest(requestId, status) { return this.post(FAMILY_API.TEACHER_AI_RESOLVE(requestId), { status }); }
  getFamilyPaymentRules() { return this.request(FAMILY_API.TEACHER_PAYMENT_RULES); }
  getFamilyPaymentModes() { return this.request(FAMILY_API.TEACHER_PAYMENT_MODES); }
  getFamilyAiSends() { return this.request(FAMILY_API.TEACHER_AI_SENDS); }
  connectStripe() { return this.post(FAMILY_API.STRIPE_CONNECT, {}); }
  getStripeStatus() { return this.request(FAMILY_API.STRIPE_STATUS); }
  // PAY1: `lessonIds` = oldest-first prefix of the due lessons.
  createFamilyCardCheckout(actor, lessonIds) {
    return this.familyPost(FAMILY_API.CARD_CHECKOUT, { ...actor, lesson_ids: lessonIds });
  }
  getFamilyCardStatus() { return this.familyRequest(FAMILY_API.CARD_STATUS); }
  // PAY1: link to the family Payer screen; `mode` = FAMILY_PAYMENT_MODE value.
  createFamilyPaymentLink({ familyId, mode, sendEmail }) {
    return this.post(FAMILY_API.TEACHER_PAYMENT_LINKS, { family_id: familyId, mode, send_email: sendEmail });
  }
  // FAM3-B R4: `allowed` null = remove the rule (back to the default).
  setFamilyPaymentRule({ familyId = null, method, allowed }) {
    return this.request(FAMILY_API.TEACHER_PAYMENT_RULES, {
      method: 'PUT', body: JSON.stringify({ family_id: familyId, method, allowed }),
    });
  }

  // ── Formations / Courses (FORM1) — own cookie; a 401 means "not signed in here" ──

  formationsRequest(endpoint, options = {}) {
    return this.request(endpoint, { ...options, skipSessionHandling: true, suppressProRedirect: true });
  }

  formationsPost(endpoint, body = {}) {
    return this.formationsRequest(endpoint, { method: 'POST', body: JSON.stringify(body) });
  }

  getFormationsOffers(locale, geoClaim = null) {
    const query = new URLSearchParams({ locale });
    if (geoClaim) {
      query.set('geoCountry', geoClaim.country);
      query.set('geoIssuedAt', String(geoClaim.issuedAt));
      query.set('geoSignature', geoClaim.signature);
    }
    return this.formationsRequest(`${FORMATIONS.OFFERS}?${query}`);
  }
  getFormationsMe() { return this.formationsRequest(FORMATIONS.ME); }
  requestFormationsLink(payload) { return this.formationsPost(FORMATIONS.AUTH_LINK, payload); }
  redeemFormationsLink(payload) { return this.formationsPost(FORMATIONS.AUTH_REDEEM, payload); }
  completeFormationsLogin(payload) { return this.formationsPost(FORMATIONS.AUTH_COMPLETE, payload); }
  logoutFormations(locale) { return this.formationsPost(FORMATIONS.AUTH_LOGOUT, { locale }); }
  getFormationsDevices(locale) { return this.formationsRequest(`${FORMATIONS.DEVICES}?locale=${locale}`); }
  revokeFormationsDevice(sessionId, locale) { return this.formationsPost(FORMATIONS.DEVICE_REVOKE(sessionId), { locale }); }
  requestFormationsAcademicCode(academicEmail, locale) {
    return this.formationsPost(FORMATIONS.ACADEMIC_CODE, { academicEmail, locale });
  }
  verifyFormationsAcademicCode(code, locale) { return this.formationsPost(FORMATIONS.ACADEMIC_VERIFY, { code, locale }); }
  createFormationsCheckout(payload) { return this.formationsPost(FORMATIONS.CHECKOUT, payload); }
  getFormationsCheckoutStatus(checkoutSessionId, locale) {
    return this.formationsRequest(`${FORMATIONS.CHECKOUT_STATUS}?session_id=${encodeURIComponent(checkoutSessionId)}&locale=${locale}`);
  }
  enrollFormationsCourse(payload) { return this.formationsPost(FORMATIONS.ENROLL, payload); }
  getFormationsLesson(courseKey, lessonId, locale) {
    return this.formationsRequest(`${FORMATIONS.LESSON(courseKey, lessonId)}?locale=${locale}`);
  }

  // ── Infrastructure / public endpoints ────────────────────────────────────

  // Server health probe — no auth required (used by NetworkContext)
  async checkHealth(signal) {
    const response = await serverRequest(`${this.getApiBase()}/health`, { method: 'GET', signal });
    return response.ok;
  }

  // Public invoice verification — no auth required
  async verifyInvoice(invoiceUuid) {
    const response = await serverRequest(`${this.getApiBase()}/invoices/verify/${invoiceUuid}`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Facture non trouvée');
    return data;
  }

  // ── External French government APIs ──────────────────────────────────────

  // BAN address autocomplete
  async searchAddress(query) {
    const response = await serverRequest(
      `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=5`,
      { external: true },
    );
    const data = await response.json();
    return data.features || [];
  }

  // Geo API — birth city lookup by postal code
  async searchBirthCity(postalCode) {
    const response = await serverRequest(
      `https://geo.api.gouv.fr/communes?codePostal=${postalCode}&fields=nom,code,codeDepartement,codesPostaux`,
      { external: true },
    );
    return response.json();
  }
}

export default new ApiService();
